<template>
  <mf-loading-dialog v-if="loading" :loading="loading" />
  <v-dialog v-else :value="value" width="1000px" @input="$emit('input', false)">
    <v-card class="coupon-card-container">
      <v-card-title class="d-flex align-baseline pa-0" :class="{ 'coupon-card-title-mobile': isMobile }">
        <div class="d-flex flex-column title-section">
          <h1>Detalhes do cupom</h1>
          <span>Você está vendo informações do cupom</span>
          <span>ID do cupom: {{ couponInfo.id }}</span>
        </div>
        <mf-search v-if="!isMobile" v-model="search" placeholder="Buscar por produto" outlined hide-details />
      </v-card-title>

      <v-divider v-if="isMobile" />

      <div class="coupon-details">
        <div>
          <span class="label">Consumidor</span>
          <span v-if="isMobile || onlyMercapromo" class="value">{{ clientId }}</span>
          <a v-else :href="`#/clientes/detalhes-cliente/${clientId}`" target="_blank" class="link">{{ clientId }}</a>
        </div>
        <div>
          <span class="label">Data</span>
          <strong>{{ getCouponDateTime }}</strong>
        </div>
        <div>
          <span class="label">Valor do cupom</span>
          <strong>R$ {{ couponInfo.revenue | localeString(2) }}</strong>
        </div>
        <div>
          <div class="d-flex align-center">
            <span class="d-block mr-1 label">Desconto concedido</span>
            <v-tooltip bottom max-width="230px">
              <template v-slot:activator="{ on }">
                <span v-on="on">
                  <mf-icon small fill color="cinza-0" style="display: table-cell; vertical-align: middle">info</mf-icon>
                </span>
              </template>
              <span
                >Valor calculado com base no cupom fiscal, utilizando o preço 1 e 2 aplicados no PDV, considerando todos os descontos aplicados (ofertas,
                vouchers, cashback etc) e o preço 1 atualizado</span
              >
            </v-tooltip>
          </div>
          <strong>R$ {{ couponInfo.discount_price | localeString(2) }}</strong>
        </div>
      </div>

      <v-divider v-if="isMobile" />
      <mf-search v-if="isMobile" v-model="search" placeholder="Buscar por produto" dense outlined hide-details />

      <v-card-text class="pa-0 flex-grow-1">
        <v-row v-if="!isMobile" no-gutters>
          <v-col>
            <v-data-table
              class="coupon-table"
              :items-per-page="5"
              :headers="headers"
              :footer-props="{ itemsPerPageOptions: [5, 10, 15] }"
              :items="filteredProducts"
              mobile-breakpoint="0"
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    <span v-if="onlyMercapromo">{{ item.product.sku }}</span>
                    <a v-else class="link" target="_blank" :href="`#/produtos/detalhes-produto/${item.product.sku}`">{{ item.product.sku }}</a>
                  </td>
                  <td>
                    <v-tooltip top max-width="250">
                      <template #activator="{ on }">
                        <span v-on="on">
                          {{ putEllipsis(item.product.name, 25) }}
                        </span>
                      </template>
                      <span>{{ item.product.name }}</span>
                    </v-tooltip>
                  </td>
                  <td>
                    {{ !item.quantity ? '-' : item.quantity | localeString(2) }}
                  </td>
                  <td>
                    {{ !item.product_with_discount_quantity ? '-' : item.product_with_discount_quantity | localeString(2) }}
                  </td>
                  <td>
                    {{ item.revenue | localeString(2) | currency }}
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-col>
        </v-row>

        <div v-if="isMobile" class="products-container">
          <div v-for="item in filteredProducts" :key="`product-key-${item.product.sku}`" class="product-item">
            <span>{{ item.product.sku }}</span>
            <span>
              <strong>{{ item.product.name }}</strong>
            </span>

            <div class="d-flex flex-row justify-space-between">
              <span>Qtd. total:</span>
              <span>{{ !item.quantity ? '-' : item.quantity | localeString(2) }}</span>
            </div>

            <div class="d-flex flex-row justify-space-between">
              <span>Qtd. com desconto:</span>
              <span>{{ !item.product_with_discount_quantity ? '-' : item.product_with_discount_quantity | localeString(2) }}</span>
            </div>

            <div class="d-flex flex-row justify-space-between">
              <span>Valor total:</span>
              <span>{{ item.revenue | localeString(2) | currency }}</span>
            </div>
          </div>
        </div>

        <mf-action-buttons
          class="mt-2"
          :primary-button="{
            text: 'Fechar',
            action: () => $emit('input', false),
            isVisible: true,
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { QUERY_GET_COUPON_ITEMS } from '@/modules/clientes/detalhes-cliente/graphql'
import { putEllipsis } from '@/config/helpers.js'
import { DateTime } from 'luxon'
export default {
  props: {
    value: Boolean,
    coupon: Object,
    clientId: String,
  },
  data: () => ({
    search: '',
    tableItems: [],
    loading: false,
    couponInfo: {},
  }),
  computed: {
    getCouponDateTime() {
      if (!this.couponInfo.purchased_at_datetime) return ''
      return DateTime.fromFormat(this.couponInfo.purchased_at_datetime, 'yyyy-MM-dd HH:mm:ss').toFormat('dd/MM/yyyy | HH') + 'h'
    },
    query() {
      return QUERY_GET_COUPON_ITEMS
    },
    headers() {
      return [
        {
          text: 'Código',
          value: 'product.sku',
          width: '15%',
        },
        {
          text: 'Produto',
          value: 'product.name',
          width: '30%',
        },
        {
          text: 'Qtd total',
          value: 'quantity',
          width: '15%',
        },
        {
          text: 'Qtd com desconto',
          value: 'quantity_discount',
          width: '20%',
        },
        {
          text: 'Valor total',
          value: 'revenue',
          width: '20%',
        },
      ]
    },
    filteredProducts() {
      return this.tableItems.filter((object) => JSON.stringify(object).toString().toLowerCase().includes(this.search.toLowerCase()))
    },
    onlyMercapromo() {
      return this.$getLoggedUser().only_mercapromo
    },
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        if (this.value) this.getCoupon()
        else {
          this.tableItems = []
          this.couponInfo = {}
        }
      },
    },
  },
  methods: {
    putEllipsis,
    async getCoupon() {
      try {
        this.loading = true
        const {
          data: { coupon },
        } = await this.$apollo.query({
          query: this.query,
          variables: {
            id: this.coupon?.id,
          },
          fetchPolicy: 'no-cache',
        })
        this.couponInfo = coupon
        this.tableItems = coupon.items
      } catch (error) {
        this.$snackbar({ message: 'Erro ao carregar o cupom, tente novamente mais tarde.', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.title-section {
  gap: 8px;
  flex: 1;

  h1 {
    font-weight: 700;
    color: $cinza-d10;
    font-size: 26px;
    line-height: 120%;
    margin: 0;
  }

  span {
    font-weight: 400;
    color: $cinza-0;
    font-size: 16px;
    line-height: 120%;
    margin: 0;
  }
}

.coupon-card-container {
  display: flex;
  flex-direction: column;
  padding: 24px 32px 16px 32px;
  gap: 16px;
}

.coupon-card-title-mobile {
  flex-direction: column;
  gap: 8px;
}

.coupon-details {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  margin-bottom: 28px;

  .label {
    display: block;
    font-size: 14px;
    color: $cinza-0;
  }

  .value,
  a,
  strong {
    display: block;
    margin-top: 4px;
    font-size: 22px !important;
    line-height: 130% !important;
    font-weight: bold;
  }

  .value,
  strong {
    color: $cinza-0;
  }
}

.coupon-table {
  max-height: 350px;
  overflow: auto;
}
::v-deep .v-input {
  max-width: 275px !important;
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.products-container {
  display: flex;
  flex-direction: column;
}

.product-item {
  display: flex;
  flex-direction: column;
  padding: 6px 12px;
  border-bottom: 1.5px dashed $cinza-l50;
}

.product-item:last-of-type {
  border-bottom: none;
}

@media screen and (max-width: 933px) {
  .title-section {
    gap: 4px;

    h1 {
      font-size: 22px;
      line-height: 100%;
    }

    span {
      font-size: 14px;
      line-height: 100%;
    }
  }

  .coupon-details {
    flex-direction: column;
    gap: 8px;
    margin: 0;

    .value,
    a,
    strong {
      margin-top: 0;
      font-size: 14px !important;
      line-height: 100% !important;
      font-weight: bold;
    }
  }

  .coupon-table {
    max-height: unset;
    overflow-y: hidden;
  }

  .coupon-card-container {
    padding: 18px;
  }
}
</style>
